import type { ReactElement } from "react";
import { cva } from "class-variance-authority";
import { cn } from "./lib/utils";
import type { VariantFunction, VariantValue } from "./variant-types";

export interface LogoVariantProps {
  variant?: VariantValue<"full" | "icon" | "text">;
}

const logoVariants: VariantFunction<LogoVariantProps> = cva("", {
  variants: {
    variant: {
      full: "",
      icon: "",
      text: "",
    },
  },
  defaultVariants: {
    variant: "full",
  },
});

export interface LogoProps extends LogoVariantProps {
  /**
   * Height in pixels (width will be auto-calculated based on aspect ratio)
   */
  height?: number;

  /**
   * Optional CSS class for additional styling
   */
  className?: string;

  /**
   * Color for the logo (defaults to currentColor)
   */
  color?: string;

  /**
   * Fallback title to display as text when no SVG logo is needed
   * When provided, renders as styled text instead of SVG
   */
  title?: string | undefined;
}

/**
 * Rizom logo component
 * Displays the Rizom branding with icon and/or text
 */
export function Logo({
  variant,
  height = 32,
  className,
  color = "currentColor",
  title,
}: LogoProps): ReactElement {
  // If title is provided, render as text
  if (title) {
    return (
      <span
        className={cn(
          "font-heading font-medium text-[1.4rem] leading-none tracking-[-0.01em] [font-variation-settings:'opsz'_24,'SOFT'_50]",
          className,
        )}
      >
        {title}
      </span>
    );
  }

  const resolvedVariant = variant ?? "full";

  if (resolvedVariant === "icon") {
    return (
      <svg
        height={height}
        viewBox="0 0 60 59"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Rizom logo"
      >
        <g id="Group">
          <path
            id="Vector"
            d="M29.8235 22.1142C31.7219 22.1142 33.5065 22.8336 34.8489 24.1412C36.1913 25.4481 36.9309 27.1856 36.9309 29.0338C36.9309 30.882 36.192 32.6195 34.8489 33.9265C33.5065 35.2334 31.7219 35.9535 29.8235 35.9535C27.9251 35.9535 26.1405 35.234 24.7981 33.9265C23.4557 32.6189 22.7167 30.882 22.7167 29.0338C22.7167 27.1856 23.4557 25.4481 24.7981 24.1412C26.1405 22.8342 27.9251 22.1142 29.8235 22.1142ZM29.8235 17.1881C23.104 17.1881 17.6562 22.4918 17.6562 29.0338C17.6562 35.5758 23.104 40.8796 29.8235 40.8796C36.543 40.8796 41.9907 35.5758 41.9907 29.0338C41.9907 22.4918 36.543 17.1881 29.8235 17.1881Z"
            fill={color}
          />
          <g id="Group_2">
            <path
              id="Vector_2"
              d="M13.2469 29.0338C13.2469 33.2709 14.9323 37.1315 17.6846 40.0136C11.8265 39.6006 7.20517 34.8424 7.20517 29.0338C7.20517 23.2253 11.8265 18.4671 17.6846 18.0541C14.9323 20.9362 13.2469 24.798 13.2469 29.0338Z"
              fill={color}
            />
            <path
              id="Vector_3"
              d="M52.4385 29.0338C52.4385 34.8424 47.8172 39.6006 41.9591 40.0136C44.7114 37.1315 46.3967 33.2709 46.3967 29.0338C46.3967 24.7968 44.7114 20.9362 41.9591 18.0541C47.8172 18.4671 52.4385 23.2253 52.4385 29.0338Z"
              fill={color}
            />
            <g id="Group_3">
              <path
                id="Vector_4"
                d="M59.7265 29.0338C59.7265 32.172 57.6563 34.84 54.7735 35.8142C56.0553 31.4628 56.056 26.6042 54.7735 22.2535C57.6563 23.2277 59.7265 25.8957 59.7265 29.0338Z"
                fill={color}
              />
              <path
                id="Vector_5"
                d="M3.97003 30.5734C4.08122 32.3778 4.3873 34.1312 4.86955 35.8139C-1.62448 33.824 -1.62136 24.2431 4.86955 22.2544C4.17929 24.8932 3.65582 27.8106 3.97003 30.5734Z"
                fill={color}
              />
            </g>
            <path
              id="Vector_6"
              d="M41.0996 40.8504C40.6754 46.5537 35.788 51.0529 29.8218 51.0529C23.8557 51.0529 18.9683 46.5537 18.5441 40.8504C21.5044 43.5299 25.471 45.1708 29.8218 45.1708C34.1726 45.1708 38.1393 43.5299 41.0996 40.8504Z"
              fill={color}
            />
            <path
              id="Vector_7"
              d="M41.0996 17.2173C38.1393 14.5377 34.1739 12.8969 29.8218 12.8969C25.4698 12.8969 21.5044 14.5377 18.5441 17.2173C18.9683 11.514 23.8557 7.01479 29.8218 7.01479C35.788 7.01479 40.6754 11.514 41.0996 17.2173Z"
              fill={color}
            />
            <g id="Group_4">
              <path
                id="Vector_8"
                d="M36.7862 53.3262C34.7417 59.6493 24.9007 59.6462 22.8574 53.3262C27.3257 54.5742 32.318 54.5742 36.7862 53.3262Z"
                fill={color}
              />
              <path
                id="Vector_9"
                d="M36.7862 4.74088C32.318 3.49294 27.3257 3.49294 22.8574 4.74088C24.902 -1.58155 34.743 -1.57851 36.7862 4.74088Z"
                fill={color}
              />
            </g>
          </g>
        </g>
      </svg>
    );
  }

  if (resolvedVariant === "text") {
    return (
      <svg
        height={height}
        viewBox="0 0 138 33"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-label="Rizom"
      >
        <g id="RIZOM">
          <path
            d="M5.24978 24.3876V31.7373H0V0.715879H10.0223C12.4086 0.715879 14.2858 1.43176 15.6539 2.86352C17.0538 4.26346 17.7538 6.01339 17.7538 8.1133C17.7538 9.99049 16.9902 11.7086 15.463 13.2676C17.7538 13.7767 19.5992 15.0494 20.9991 17.0857C22.3991 19.1219 23.099 21.54 23.099 24.3399V31.7373H17.8493V24.8172C17.8493 22.5263 17.2606 20.7287 16.0834 19.4242C14.938 18.1197 13.4108 17.4675 11.5018 17.4675C9.72005 17.4675 8.22466 18.0879 7.01562 19.3287C5.83839 20.5696 5.24978 22.2559 5.24978 24.3876ZM9.11553 5.82249H5.24978V14.7948C5.4725 14.5721 5.72704 14.3494 6.01339 14.1267C6.29974 13.904 6.602 13.6972 6.92017 13.5063C7.27015 13.3154 7.55651 13.1563 7.77922 13.029C8.03376 12.8699 8.36783 12.6949 8.78145 12.504C9.19507 12.2813 9.49733 12.1222 9.68824 12.0268C11.5972 11.0086 12.5518 9.86323 12.5518 8.59055C12.5518 7.7315 12.2495 7.06334 11.645 6.58609C11.0405 6.07702 10.1973 5.82249 9.11553 5.82249Z"
            fill={color}
          />
          <path
            d="M33.3536 0.715879V31.7373H28.1039V0.715879H33.3536Z"
            fill={color}
          />
          <path
            d="M61.3617 0.715879L46.71 26.5353H61.5049V31.7373H37.6422L52.3416 5.91794H39.3603V0.715879H61.3617Z"
            fill={color}
          />
          <path
            d="M79.0133 32.4055C74.5908 32.4055 70.8205 30.8305 67.7024 27.6807C64.6162 24.5308 63.0731 20.6969 63.0731 16.1789C63.0731 11.7245 64.6162 7.9224 67.7024 4.77253C70.8205 1.59084 74.5908 0 79.0133 0C83.4359 0 87.2062 1.59084 90.3242 4.77253C93.4741 7.9224 95.049 11.7245 95.049 16.1789C95.049 20.6969 93.49 24.5308 90.3719 27.6807C87.2539 30.8305 83.4677 32.4055 79.0133 32.4055ZM79.0133 27.0125C81.9723 27.0125 84.4699 25.9626 86.5062 23.8626C88.5743 21.7627 89.6083 19.2015 89.6083 16.1789C89.6083 13.1881 88.5743 10.6586 86.5062 8.59055C84.4699 6.49064 81.9723 5.44068 79.0133 5.44068C76.0862 5.44068 73.6045 6.49064 71.5682 8.59055C69.5319 10.6586 68.5138 13.1881 68.5138 16.1789C68.5138 19.2015 69.5319 21.7627 71.5682 23.8626C73.6045 25.9626 76.0862 27.0125 79.0133 27.0125Z"
            fill={color}
          />
          <path
            d="M126.682 0C129.545 0 132.011 1.04996 134.079 3.14987C136.147 5.21796 137.182 7.82695 137.182 10.9768V31.6896H131.932V10.7859C131.932 9.29052 131.423 8.01785 130.405 6.96789C129.386 5.91794 128.146 5.39296 126.682 5.39296C125.25 5.39296 124.025 5.90203 123.007 6.92017C121.989 7.93831 121.48 9.14735 121.48 10.5473V31.7373H116.182L116.23 10.5473C116.23 9.17917 115.705 7.98603 114.655 6.96789C113.605 5.91794 112.364 5.39296 110.933 5.39296C109.437 5.39296 108.165 5.91794 107.115 6.96789C106.065 8.01785 105.54 9.29052 105.54 10.7859V31.7373H100.338L100.29 10.9768C100.29 7.82695 101.324 5.21796 103.392 3.14987C105.492 1.04996 108.005 0 110.933 0C114.146 0 116.771 1.24086 118.807 3.72257C120.844 1.24086 123.468 0 126.682 0Z"
            fill={color}
          />
        </g>
      </svg>
    );
  }

  // Full logo (icon + text)
  const iconHeight = height;
  const textHeight = (height * 33) / 59; // Maintain aspect ratio relative to icon
  const gap = height * 0.2; // 20% of height as gap

  return (
    <div
      className={cn("inline-flex items-center", className)}
      style={{ gap: `${gap}px` }}
      aria-label="Rizom logo"
    >
      <svg
        height={iconHeight}
        viewBox="0 0 60 59"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Group">
          <path
            id="Vector"
            d="M29.8235 22.1142C31.7219 22.1142 33.5065 22.8336 34.8489 24.1412C36.1913 25.4481 36.9309 27.1856 36.9309 29.0338C36.9309 30.882 36.192 32.6195 34.8489 33.9265C33.5065 35.2334 31.7219 35.9535 29.8235 35.9535C27.9251 35.9535 26.1405 35.234 24.7981 33.9265C23.4557 32.6189 22.7167 30.882 22.7167 29.0338C22.7167 27.1856 23.4557 25.4481 24.7981 24.1412C26.1405 22.8342 27.9251 22.1142 29.8235 22.1142ZM29.8235 17.1881C23.104 17.1881 17.6562 22.4918 17.6562 29.0338C17.6562 35.5758 23.104 40.8796 29.8235 40.8796C36.543 40.8796 41.9907 35.5758 41.9907 29.0338C41.9907 22.4918 36.543 17.1881 29.8235 17.1881Z"
            fill={color}
          />
          <g id="Group_2">
            <path
              id="Vector_2"
              d="M13.2469 29.0338C13.2469 33.2709 14.9323 37.1315 17.6846 40.0136C11.8265 39.6006 7.20517 34.8424 7.20517 29.0338C7.20517 23.2253 11.8265 18.4671 17.6846 18.0541C14.9323 20.9362 13.2469 24.798 13.2469 29.0338Z"
              fill={color}
            />
            <path
              id="Vector_3"
              d="M52.4385 29.0338C52.4385 34.8424 47.8172 39.6006 41.9591 40.0136C44.7114 37.1315 46.3967 33.2709 46.3967 29.0338C46.3967 24.7968 44.7114 20.9362 41.9591 18.0541C47.8172 18.4671 52.4385 23.2253 52.4385 29.0338Z"
              fill={color}
            />
            <g id="Group_3">
              <path
                id="Vector_4"
                d="M59.7265 29.0338C59.7265 32.172 57.6563 34.84 54.7735 35.8142C56.0553 31.4628 56.056 26.6042 54.7735 22.2535C57.6563 23.2277 59.7265 25.8957 59.7265 29.0338Z"
                fill={color}
              />
              <path
                id="Vector_5"
                d="M3.97003 30.5734C4.08122 32.3778 4.3873 34.1312 4.86955 35.8139C-1.62448 33.824 -1.62136 24.2431 4.86955 22.2544C4.17929 24.8932 3.65582 27.8106 3.97003 30.5734Z"
                fill={color}
              />
            </g>
            <path
              id="Vector_6"
              d="M41.0996 40.8504C40.6754 46.5537 35.788 51.0529 29.8218 51.0529C23.8557 51.0529 18.9683 46.5537 18.5441 40.8504C21.5044 43.5299 25.471 45.1708 29.8218 45.1708C34.1726 45.1708 38.1393 43.5299 41.0996 40.8504Z"
              fill={color}
            />
            <path
              id="Vector_7"
              d="M41.0996 17.2173C38.1393 14.5377 34.1739 12.8969 29.8218 12.8969C25.4698 12.8969 21.5044 14.5377 18.5441 17.2173C18.9683 11.514 23.8557 7.01479 29.8218 7.01479C35.788 7.01479 40.6754 11.514 41.0996 17.2173Z"
              fill={color}
            />
            <g id="Group_4">
              <path
                id="Vector_8"
                d="M36.7862 53.3262C34.7417 59.6493 24.9007 59.6462 22.8574 53.3262C27.3257 54.5742 32.318 54.5742 36.7862 53.3262Z"
                fill={color}
              />
              <path
                id="Vector_9"
                d="M36.7862 4.74088C32.318 3.49294 27.3257 3.49294 22.8574 4.74088C24.902 -1.58155 34.743 -1.57851 36.7862 4.74088Z"
                fill={color}
              />
            </g>
          </g>
        </g>
      </svg>
      <svg
        height={textHeight}
        viewBox="0 0 138 33"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="RIZOM">
          <path
            d="M5.24978 24.3876V31.7373H0V0.715879H10.0223C12.4086 0.715879 14.2858 1.43176 15.6539 2.86352C17.0538 4.26346 17.7538 6.01339 17.7538 8.1133C17.7538 9.99049 16.9902 11.7086 15.463 13.2676C17.7538 13.7767 19.5992 15.0494 20.9991 17.0857C22.3991 19.1219 23.099 21.54 23.099 24.3399V31.7373H17.8493V24.8172C17.8493 22.5263 17.2606 20.7287 16.0834 19.4242C14.938 18.1197 13.4108 17.4675 11.5018 17.4675C9.72005 17.4675 8.22466 18.0879 7.01562 19.3287C5.83839 20.5696 5.24978 22.2559 5.24978 24.3876ZM9.11553 5.82249H5.24978V14.7948C5.4725 14.5721 5.72704 14.3494 6.01339 14.1267C6.29974 13.904 6.602 13.6972 6.92017 13.5063C7.27015 13.3154 7.55651 13.1563 7.77922 13.029C8.03376 12.8699 8.36783 12.6949 8.78145 12.504C9.19507 12.2813 9.49733 12.1222 9.68824 12.0268C11.5972 11.0086 12.5518 9.86323 12.5518 8.59055C12.5518 7.7315 12.2495 7.06334 11.645 6.58609C11.0405 6.07702 10.1973 5.82249 9.11553 5.82249Z"
            fill={color}
          />
          <path
            d="M33.3536 0.715879V31.7373H28.1039V0.715879H33.3536Z"
            fill={color}
          />
          <path
            d="M61.3617 0.715879L46.71 26.5353H61.5049V31.7373H37.6422L52.3416 5.91794H39.3603V0.715879H61.3617Z"
            fill={color}
          />
          <path
            d="M79.0133 32.4055C74.5908 32.4055 70.8205 30.8305 67.7024 27.6807C64.6162 24.5308 63.0731 20.6969 63.0731 16.1789C63.0731 11.7245 64.6162 7.9224 67.7024 4.77253C70.8205 1.59084 74.5908 0 79.0133 0C83.4359 0 87.2062 1.59084 90.3242 4.77253C93.4741 7.9224 95.049 11.7245 95.049 16.1789C95.049 20.6969 93.49 24.5308 90.3719 27.6807C87.2539 30.8305 83.4677 32.4055 79.0133 32.4055ZM79.0133 27.0125C81.9723 27.0125 84.4699 25.9626 86.5062 23.8626C88.5743 21.7627 89.6083 19.2015 89.6083 16.1789C89.6083 13.1881 88.5743 10.6586 86.5062 8.59055C84.4699 6.49064 81.9723 5.44068 79.0133 5.44068C76.0862 5.44068 73.6045 6.49064 71.5682 8.59055C69.5319 10.6586 68.5138 13.1881 68.5138 16.1789C68.5138 19.2015 69.5319 21.7627 71.5682 23.8626C73.6045 25.9626 76.0862 27.0125 79.0133 27.0125Z"
            fill={color}
          />
          <path
            d="M126.682 0C129.545 0 132.011 1.04996 134.079 3.14987C136.147 5.21796 137.182 7.82695 137.182 10.9768V31.6896H131.932V10.7859C131.932 9.29052 131.423 8.01785 130.405 6.96789C129.386 5.91794 128.146 5.39296 126.682 5.39296C125.25 5.39296 124.025 5.90203 123.007 6.92017C121.989 7.93831 121.48 9.14735 121.48 10.5473V31.7373H116.182L116.23 10.5473C116.23 9.17917 115.705 7.98603 114.655 6.96789C113.605 5.91794 112.364 5.39296 110.933 5.39296C109.437 5.39296 108.165 5.91794 107.115 6.96789C106.065 8.01785 105.54 9.29052 105.54 10.7859V31.7373H100.338L100.29 10.9768C100.29 7.82695 101.324 5.21796 103.392 3.14987C105.492 1.04996 108.005 0 110.933 0C114.146 0 116.771 1.24086 118.807 3.72257C120.844 1.24086 123.468 0 126.682 0Z"
            fill={color}
          />
        </g>
      </svg>
    </div>
  );
}

export { logoVariants };
